<script setup>
import { ref, computed, inject, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { EditorView, keymap, lineNumbers, drawSelection, highlightActiveLine, highlightActiveLineGutter } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { javascript } from '@codemirror/lang-javascript';
import { foldGutter, foldKeymap, bracketMatching, indentOnInput } from '@codemirror/language';
import { oneDark } from '@codemirror/theme-one-dark';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  return [...base, ...flowDynamicVariables.value];
});

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

const varName = computed(() => props.modelValue?.variable_name ?? '');
const hintVarPath = computed(() => `{{variables.${varName.value || 'resultado'}}}`);

// Variable picker state
const openPicker = ref(false);
const pickerSearch = ref('');

const filteredVars = computed(() => {
  const q = pickerSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q));
});

function togglePicker() {
  openPicker.value = !openPicker.value;
  pickerSearch.value = '';
}

function insertVar(key) {
  const wrapped = `{{${key}}}`;
  if (view) {
    const sel = view.state.selection.main;
    view.dispatch({
      changes: { from: sel.from, to: sel.to, insert: wrapped },
      selection: { anchor: sel.from + wrapped.length },
    });
    view.focus();
  }
  openPicker.value = false;
  pickerSearch.value = '';
}

// CodeMirror editor
const container = ref(null);
let view = null;
let ignoreNextUpdate = false;

function isDarkMode() {
  return document.documentElement.classList.contains('dark');
}

const lightTheme = EditorView.theme({
  '&': {
    fontSize: '12px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
  '.cm-scroller': { overflow: 'auto', maxHeight: '300px' },
  '.cm-content': { padding: '8px 0', minHeight: '120px' },
  '.cm-line': { padding: '0 8px' },
  '.cm-gutters': {
    backgroundColor: '#f8f9fa',
    borderRight: '1px solid #e2e8f0',
    color: '#94a3b8',
  },
  '.cm-activeLineGutter': { backgroundColor: '#f1f5f9' },
  '.cm-activeLine': { backgroundColor: '#f8fafc' },
  '.cm-foldGutter': { width: '14px' },
});

const darkTheme = EditorView.theme({
  '&': {
    fontSize: '12px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
  '.cm-scroller': { overflow: 'auto', maxHeight: '300px' },
  '.cm-content': { padding: '8px 0', minHeight: '120px' },
  '.cm-line': { padding: '0 8px' },
  '.cm-foldGutter': { width: '14px' },
}, { dark: true });

function buildExtensions() {
  return [
    lineNumbers(),
    highlightActiveLine(),
    highlightActiveLineGutter(),
    drawSelection(),
    foldGutter(),
    bracketMatching(),
    indentOnInput(),
    history(),
    keymap.of([...defaultKeymap, ...historyKeymap, ...foldKeymap]),
    javascript(),
    EditorView.lineWrapping,
    isDarkMode() ? [oneDark, darkTheme] : lightTheme,
    EditorView.updateListener.of(upd => {
      if (upd.docChanged) {
        ignoreNextUpdate = true;
        update({ code: upd.state.doc.toString() });
      }
    }),
  ];
}

onMounted(() => {
  view = new EditorView({
    state: EditorState.create({
      doc: props.modelValue?.code ?? '',
      extensions: buildExtensions(),
    }),
    parent: container.value,
  });
});

onUnmounted(() => {
  view?.destroy();
  view = null;
});

watch(() => props.modelValue?.code, val => {
  if (!view || ignoreNextUpdate) {
    ignoreNextUpdate = false;
    return;
  }
  const current = view.state.doc.toString();
  if (current !== val) {
    view.dispatch({ changes: { from: 0, to: current.length, insert: val ?? '' } });
  }
});
</script>

<template>
  <div class="flex flex-col gap-4">

    <!-- Code editor -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.CODE.CODE_LABEL') }}</span>
      <div
        ref="container"
        class="rounded-lg border border-n-weak overflow-hidden focus-within:ring-1 focus-within:ring-n-brand focus-within:border-n-brand transition-colors"
      />
      <div class="flex justify-end">
        <div class="relative">
          <button
            type="button"
            class="inline-flex items-center justify-center gap-1.5 px-2.5 h-7 rounded-lg text-xs text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors border border-n-weak"
            @click.stop="togglePicker"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
            {{ t('FLOW_BUILDER.COMMON.INSERT_VARIABLE') }}
          </button>
          <div
            v-if="openPicker"
            class="absolute bottom-full right-0 mb-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
          >
            <div class="px-2 pt-2 pb-1">
              <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
                <input
                  v-model="pickerSearch"
                  type="text"
                  class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                  :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                  autofocus
                />
              </div>
            </div>
            <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
              <button
                v-for="v in filteredVars"
                :key="v.key"
                type="button"
                class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="insertVar(v.key)"
              >
                {{ resolveLabel(v.label) }}
              </button>
              <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="h-px bg-n-weak -mx-4" />

    <!-- Output variable -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.CODE.VARIABLE_NAME') }}</span>
      <input
        type="text"
        class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
        :placeholder="t('FLOW_BUILDER.ACTION_FORMS.CODE.VARIABLE_NAME_PLACEHOLDER')"
        :value="varName"
        @input="update({ variable_name: $event.target.value })"
      />
      <p class="text-xs text-n-slate-9">
        {{ t('FLOW_BUILDER.ACTION_FORMS.CODE.VARIABLE_HINT_BEFORE') }} <code class="text-n-slate-11">{{ hintVarPath }}</code> {{ t('FLOW_BUILDER.ACTION_FORMS.CODE.VARIABLE_HINT_AFTER') }}
      </p>
    </div>

    <!-- Outputs -->
    <div class="flex flex-col gap-1.5 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
      <div class="flex items-center gap-2 text-xs text-n-slate-9">
        <span class="inline-block size-2 rounded-full bg-n-teal-9 shrink-0" />
        <span><strong class="text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.CODE.OUTPUT_OK_LABEL') }}</strong> {{ t('FLOW_BUILDER.ACTION_FORMS.CODE.OUTPUT_OK_DESC') }}</span>
      </div>
      <div class="flex items-center gap-2 text-xs text-n-slate-9">
        <span class="inline-block size-2 rounded-full bg-n-ruby-9 shrink-0" />
        <span><strong class="text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.CODE.OUTPUT_ERROR_LABEL') }}</strong> {{ t('FLOW_BUILDER.ACTION_FORMS.CODE.OUTPUT_ERROR_DESC') }}</span>
      </div>
    </div>

  </div>
</template>
